import {_, classNames, IDisposer, Iframe, observable, observer, React, useAsObservableSource, useEffect} from '@api'
import {IJsonTabNode, TabNode, TabSetNode} from '@flexlayout'
import {LoadingIndicator, StoresInstanceContext, useVipLocalStore, utils, VipLocalStore} from '@modules'
import {BaseTool, DEFAULT_WHITEBOARD_TAB_SETTINGS, TDSnapshot, WhiteboardPagesState,  WhiteboardRoot} from '@modules/whiteboard'
import {globalStores, IBaseFlexLayoutTabConfig, IFlexLayoutTabProps, StoresInstance} from '@stores'
import {TLBounds, TLPage, TLPageState, Utils} from '@tldraw/core'
import {when} from 'mobx'
import * as css from './WhiteboardFlexLayoutTab.css'
import {TDShape, TDShapeType, TDBinding} from './shapes/types'
import {ColorStyle, DashStyle, createDefaultStyle, ShapeStyles, SizeStyle} from './shapes/shared/shape-styles'

/// region Store

export interface IFlexLayoutWhiteboardTabConfigSettings {
  pages: WhiteboardPagesState
  meta?: {isDarkMode: boolean}

  ui?: {
    currentPageId?: string
    isDarkMode: boolean
    isDebugMode: boolean
    isPenMode: boolean
    isReadonlyMode: boolean
    isZoomSnap: boolean
    nudgeDistanceSmall: number
    nudgeDistanceLarge: number
    isFocusMode: boolean
    isSnapping: boolean
    showRotateHandles: boolean
    showBindingHandles: boolean
    showCloneHandles: boolean
    gridSize: number
    showGrid: boolean

    viewport?: TLBounds
    clipboard?: {
      shapes: TDShape[]
      bindings: TDBinding[]
    }
    selectHistory?: {
      stack?: string[][],
      pointer?: number,
    },

    rotationInfo: {
      selectedIds?: string[],
      center?: number[],
    }

    pasteInfo?: {
      center?: number[]
      offset?:  number[]
    }

    currentStyle: ShapeStyles
  }
}

export interface IFlexLayoutWhiteboardTabConfig extends IBaseFlexLayoutTabConfig {
  whiteboard: IFlexLayoutWhiteboardTabConfigSettings
}

export class WhiteboardFlexLayoutTabStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: Omit<MyProps, 'className'>) {
    super(stores)
  }

  @observable.ref localStores: StoresInstance

  get node() { return this.props.flexNode as TabNode}

  registerEffects = () => {
    const _toDispose: Array<IDisposer> = [
      when(() => this.tabConfig != null, () => {
        const {tabConfig} = this
        tabConfig.whiteboard = _.merge({}, _.pick(DEFAULT_WHITEBOARD_TAB_SETTINGS, ['ui']), tabConfig.whiteboard),

        this.localStores = new StoresInstance(`Whiteboard tab '${this.tab.id}'`, null, null, 'whiteboard', {tab: this.tab})
        this.localStores.vipTabs = this.stores.vipTabs
      }),
      this.node.autorun_updateFlexNodeCustomSettings()
    ]

    return () => {
      _toDispose.forEach(d => d())
    }
  }

  get tab() { return this.props.flexNode as TabNode}
  get tabConfig() : IFlexLayoutWhiteboardTabConfig{
    return this.tab._attributes.config as IFlexLayoutWhiteboardTabConfig
  }

  get tabSettings() { return this.tabConfig.whiteboard}

  setAsActiveTab = () => {
    this.flexLayout.settings.activeTab = this.tab.id
  }
}

export const WhiteboardFlexLayoutTabStoreContext = React.createContext<WhiteboardFlexLayoutTabStore>(null)
export const useWhiteboardFlexLayoutTabStore = () => React.useContext(WhiteboardFlexLayoutTabStoreContext)

/// endregion

/// region Components

interface MyProps extends IFlexLayoutTabProps {
  className?: string
}

export const WhiteboardFlexLayoutTab = observer<MyProps>(function WhiteboardFlexLayoutTab({className, flexNode, ...props}) {
  const obsProps = useAsObservableSource({flexNode}),
    store = useVipLocalStore((stores) => new WhiteboardFlexLayoutTabStore(stores, obsProps)),
    {localStores, setAsActiveTab, props: {flexNode: tab}, tabConfig, tabConfig: {whiteboard: settings}} = store

  useEffect(() => store.registerEffects(), [])

  return (
    <WhiteboardFlexLayoutTabStoreContext.Provider value={store}>
      <div className={classNames(css.root, className)}
           onMouseDown={setAsActiveTab}
           data-type='Whiteboard'
           tabIndex={0}>
        <div className={css.contents}>
          {!settings || !localStores ? <LoadingIndicator />
                     : <StoresInstanceContext.Provider value={localStores}>
             <WhiteboardRoot key={tab.id} />
           </StoresInstanceContext.Provider>}
        </div>
      </div>
    </WhiteboardFlexLayoutTabStoreContext.Provider>)
})

/// endregion

export function createWhiteboardTab({tab, tabSet}: {tab?: TabNode, tabSet?: TabSetNode} = {}) {
  const {flexLayout: {create:{createOrUpdateTabView}}} = globalStores

  const newTab : IJsonTabNode = {
    type: 'tab',
    component: 'whiteboard',
    name: 'Whiteboard',
    config: {
      whiteboard: _.cloneDeep(DEFAULT_WHITEBOARD_TAB_SETTINGS)
    } as IFlexLayoutWhiteboardTabConfig
  }

  if (tab?.catalogRoot) {
    return tab.catalogRoot.addNewChildTab(newTab)
  } else {
    return createOrUpdateTabView({tabSet, tabJson: newTab, action: 'new'})
  }
}



// WEBPACK FOOTER //
// ./src/modules/whiteboard/WhiteboardFlexLayoutTab.tsx